/**
 * 客户端请求超时(ADV-LOCK-1,2026-08-18 P0)。
 *
 * ★病灶:httpClient 此前**零 timeout、零 AbortController** —— 所有「发请求 → finally 解锁」
 * 的 UI(军师输入框首当其冲)把解锁完全押在 fetch 会 settle 上。只要那一发请求永不返回
 * (网络抖动 / 连接被中间设备吞掉 / 休眠恢复),finally 永不执行 → 输入框永久禁用,只能刷新。
 * 构造性实证:拦截 resume 永不响应 → 90 秒仍锁死;同点位返 502 → 5 秒内解锁。
 * 「无响应」与「有错」是两回事,只有前者会把 UI 卡死。
 *
 * ★超时值**单一源**在这里,别散落到调用点。改后端 budget 时这里要跟着改。
 */

/**
 * 军师类(/advisor/*):后端 turn 预算 120s(ai 侧 turn budget)+ 网络与序列化余量 ≈ 150s。
 * 比后端预算短会把「后端正在正常干活」误判成超时;长太多则用户干等。
 */
export const ADVISOR_TIMEOUT_MS = 150_000;

/**
 * 上传类(FormData:产品图/首帧/BGM mp3/知识库文档):走的是用户上行带宽,
 * 30s 在弱网下必然误杀正常上传 —— 这类给 5 分钟,只兜「永不返回」不兜「传得慢」。
 */
export const UPLOAD_TIMEOUT_MS = 300_000;

/**
 * 长图·读懂(`/poster/longimage/canvas/comprehend`,同步 LLM/VLM 跳):
 * 文本跳 ai 侧预算 60s(comprehend._TIMEOUT_S)× 模型链兜底两腿,带图再加 VLM 跳(每图 60s 预算)。
 * ★0823 真机抓的现行:827 字纯文本实测 35.5s / 42.2s,默认 30s 闸把「服务端正常在跑」
 * 判成 request_timeout,长 brief 智能编排 0/1 走不通 —— ADV-LOCK-1 加闸那次没有给这类
 * 分钟级同步跳留豁免档(军师/上传都留了)。推导与军师同款:比后端预算短 = 误杀,长太多 = 干等。
 * ★只豁免这一条路径,不放宽 `/poster/longimage` 前缀 —— plan-async 是秒级入队,照旧 30s。
 */
export const LONGIMAGE_COMPREHEND_TIMEOUT_MS = 150_000;

/**
 * 建站舱三个同步 LLM 跳(`/website/sites/{id}/collect/prefill`、`…/draft/generate`、`…/content/rewrite`):
 * api 调 ai 走独立的 120s 连接池、不重试(api `AiServiceTransport.postWebsite`,预填一次要回 20 来个字段 ×
 * 8000 token,几十秒是常态)。推导同军师:后端预算 120s + 余量。30s 闸会在 api 还在跑、ai 已开票时把页面判成
 * 「网络没回应」—— 用户重试 = 再付一次预填 / 出稿的钱。
 * ★只豁免这三条精确的尾段,站点列表 / 清单 / 答题 / 保存照旧 30s。
 */
export const WEBSITE_AI_TIMEOUT_MS = 150_000;
/** 其余接口:列表/详情/开关这类,30s 已经远超正常响应(P99 也是秒级)。 */
export const DEFAULT_TIMEOUT_MS = 30_000;

/** 军师路径前缀(与 backend `/api/v1/advisor/**` 对齐)。 */
const ADVISOR_PREFIX = "/advisor";

/** 长图读懂的精确路径(只此一条,见 LONGIMAGE_COMPREHEND_TIMEOUT_MS 头注)。 */
const LONGIMAGE_COMPREHEND_PATH = "/poster/longimage/canvas/comprehend";
// 获客「发现一次」(`/acq/discover/run`)0927 起异步:发起立即返回、结果靠 `GET /acq/discover/runs/{id}` 轮询,
// 两口都是秒级,走默认 30s —— 此前给它的 5 分钟豁免档连同「网关先掐 = 后台还在跑」那套判据一起删了。
/** 建站舱三个同步 LLM 跳(见 WEBSITE_AI_TIMEOUT_MS);站点 id 段不含 `/`(websiteService 做过 encodeURIComponent)。 */
const WEBSITE_AI_PATH =
  /^\/website\/sites\/[^/]+\/(?:collect\/prefill|draft\/generate|content\/rewrite)$/;

export function timeoutMsFor(path: string, body?: unknown): number {
  if (body instanceof FormData) return UPLOAD_TIMEOUT_MS;
  if (path === ADVISOR_PREFIX || path.startsWith(`${ADVISOR_PREFIX}/`)) return ADVISOR_TIMEOUT_MS;
  if (path === LONGIMAGE_COMPREHEND_PATH) return LONGIMAGE_COMPREHEND_TIMEOUT_MS;
  if (WEBSITE_AI_PATH.test(path)) return WEBSITE_AI_TIMEOUT_MS;
  return DEFAULT_TIMEOUT_MS;
}

/**
 * 超时错误:**可 catch 的普通错误**,好让既有 `catch → setError` / `finally → setLoading(false)`
 * 原路生效(这正是修复的关键 —— 不新建一条错误通道,让锁自己解开)。
 */
export class RequestTimeoutError extends Error {
  readonly path: string;
  readonly elapsedMs: number;
  readonly traceId?: string;

  constructor(path: string, elapsedMs: number, traceId?: string) {
    super("网络没回应,已解除锁定,可重试");
    this.name = "RequestTimeoutError";
    this.path = path;
    this.elapsedMs = elapsedMs;
    this.traceId = traceId;
  }
}

/**
 * 断网 / 连接被重置 / DNS 失败(fetch 抛 TypeError「Failed to fetch」)。0926 修复波 E:
 * 此前原样抛出,用户看到的是英文「Failed to fetch」或「Load failed」。
 * 同 {@link RequestTimeoutError}:普通可 catch 的错误,不新开错误通道;公共错误层据类型给人话。
 */
export class NetworkError extends Error {
  readonly path: string;
  readonly traceId?: string;

  constructor(path: string, traceId?: string) {
    super("请求没发出去,请检查网络连接后重试");
    this.name = "NetworkError";
    this.path = path;
    this.traceId = traceId;
  }
}
